import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { InspectionPlanService } from '../../../service/api/inspectionplan-service';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { selectStatusLoading } from '../../../core/redux/status/selectors';
const { INSPECTIONPLAN_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'inspectionplan';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
    status: {
      value: 'In Production',
      label: 'In Production',
    },
    startDate: null,
    endDate: null,
  },
  filterVisible: false,
  tileView: true,
  selectedInspectionPlan: null,
  editModalVisible: false,
};

export const fetchInspectionPlans = createAsyncThunk(
  'inspectionplan/fetchInspectionPlans',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.getAllInspectionPlan(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Inspection Plans');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Inspection Plans');
    }
  },
);

export const addInspectionPlan = createAsyncThunk(
  'inspectionplan/addInspectionPlan',
  async (inspectionPlanPayload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.createInspectionPlan(inspectionPlanPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Inspection Plans');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Inspection Plans');
    }
  },
);

export const editInspectionPlan = createAsyncThunk(
  'inspectionplan/editInspectionPlan',
  async (inspectionPlanPayload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.createInspectionPlan(inspectionPlanPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update inspectionPlan');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update inspectionPlan');
    }
  },
);

export const extendInspectionPlan = createAsyncThunk(
  'inspectionplan/extendInspectionPlan',
  async (inspectionPlanPayload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.extendInspectionPlan(inspectionPlanPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update inspectionPlan');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update inspectionPlan');
    }
  },
);

export const downloadInspectionPlans = createAsyncThunk(
  'inspectionplan/downloadInspectionPlans',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.downloadInspectionPlan(filters);
      const blob = response;
      saveAs(blob, `InspectionPlans.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Inspection Plans');
    }
  },
);

export const deleteInspectionPlan = createAsyncThunk(
  'inspectionplan/deleteInspectionPlan',
  async (inspectionPlanId, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.deleteInspectionPlan(inspectionPlanId);
      if (response.status === 200) {
        return inspectionPlanId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete inspectionPlan');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete inspectionPlan');
    }
  },
);

export const deleteInspectionPlanByMasterId = createAsyncThunk(
  'inspectionplan/deleteInspectionPlanByMasterId',
  async (inspectionPlanMasterId, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.deleteInspectionPlansByMasterId(inspectionPlanMasterId);
      if (response.status === 200) {
        return inspectionPlanMasterId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete inspection plan');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete inspection plan');
    }
  },
);

export const fetchInspectionPlansWithFilters = () => (dispatch, getState) => {
  const filters = getState().inspectionplan.filters;
  dispatch(
    fetchInspectionPlans({
      // searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      inspectionPlanDate: filters?.shipDate,
      partId: 0, // Get all records - stored procedure: @PartId = 0 returns all
    }),
  );
};

export const downloadInspectionPlansWithFilters = () => (dispatch, getState) => {
  const filters = getState().inspectionPlan.filters;
  dispatch(
    downloadInspectionPlans({
      searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      inspectionPlanDate: filters?.shipDate,
    }),
  );
};

export const fetchInspectionPlanData = createAsyncThunk(
  'inspectionplan/fetchInspectionPlanData',
  async (inspectionPlan, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.getInspectionPlanById(
        inspectionPlan.inspectionPlanId,
      );
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load shipment data');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load shipment data');
    }
  },
);

export const quarantineInspectionPlan = createAsyncThunk(
  'inspectionplan/quarantineinv',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.Quarantine(payload);
      if (response.status === 200) {
        return payload;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to quarantine');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to quarantine');
    }
  },
);

export const releaseInspectionPlan = createAsyncThunk(
  'inspectionplan/releaseinv',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.Release(payload);
      if (response.status === 200) {
        return payload;
      } else {
        return rejectWithValue(
          response.errorMessage?.message || 'Failed to release inspectionPlan',
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to release inspectionPlan');
    }
  },
);

export const saveInspectionParameters = createAsyncThunk(
  'inspectionplan/saveParameters',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.saveInspectionParameters(payload);
      if (response.status === 200) {
        dispatch(showToast({ message: 'Parameters saved successfully', type: 'success' }));
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to save parameters');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save parameters');
    }
  },
);

export const fetchInspectionParametersByPartId = createAsyncThunk(
  'inspectionplan/fetchParameters',
  async ({ inspectionPlanMasterId, partId }, { rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.getInspectionParametersByPartId(inspectionPlanMasterId, partId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load parameters');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load parameters');
    }
  },
);

export const softDeleteInspectionQuestion = createAsyncThunk(
  'inspectionplan/softDeleteQuestion',
  async (questionId, { dispatch, rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.softDeleteQuestion(questionId);
      if (response.status === 200) {
        dispatch(showToast({ message: 'Question deleted successfully', type: 'success' }));
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete question');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete question');
    }
  },
);

export const softDeleteInspectionResponse = createAsyncThunk(
  'inspectionplan/softDeleteResponse',
  async (responseId, { dispatch, rejectWithValue }) => {
    try {
      const response = await InspectionPlanService.softDeleteResponse(responseId);
      if (response.status === 200) {
        dispatch(showToast({ message: 'Response deleted successfully', type: 'success' }));
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete response');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete response');
    }
  },
);

export const { reducer, actions } = createSlice({
  name: 'inspectionPlan',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setCustomer: (state, action) => {
      state.filters.customer = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchInspectionPlans.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInspectionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchInspectionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addInspectionPlan.fulfilled, (state, action) => {
        // Data will be refetched via listener, no need to manually update state
      })
      .addCase(addInspectionPlan.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(extendInspectionPlan.fulfilled, (state, action) => {
        // Data will be refetched via listener, no need to manually update state
      })
      .addCase(extendInspectionPlan.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editInspectionPlan.fulfilled, (state, action) => {
        // Data will be refetched via listener, no need to manually update state
      })
      .addCase(editInspectionPlan.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchInspectionPlanData.pending, state => {
        state.inspectionPlanLoading = true;
        state.inspectionPlanError = null;
      })
      .addCase(fetchInspectionPlanData.fulfilled, (state, action) => {
        state.inspectionPlanLoading = false;
        state.inspectionPlanData = action.payload;
      })
      .addCase(fetchInspectionPlanData.rejected, (state, action) => {
        state.inspectionPlanLoading = false;
        state.inspectionPlanError = action.payload;
      })
      .addCase(deleteInspectionPlan.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      })
      .addCase(deleteInspectionPlanByMasterId.fulfilled, (state, action) => {
        // Filter out all inspection plans with the given master ID
        const newData = state.data.filter(s => s.inspectionPlanMasterId !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(
    addInspectionPlan.rejected,
    editInspectionPlan.rejected,
    fetchInspectionPlanData.rejected,
    quarantineInspectionPlan.rejected,
    releaseInspectionPlan.rejected,
    extendInspectionPlan.rejected,
    deleteInspectionPlanByMasterId.rejected,
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(
    addInspectionPlan.fulfilled,
    extendInspectionPlan.fulfilled,
    editInspectionPlan.fulfilled,
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(INSPECTIONPLAN_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'InspectionPlan saved successfully',
        type: 'success',
      }),
    );
    // Refetch the data to ensure UI is in sync with backend
    listenerApi.dispatch(fetchInspectionPlansWithFilters());
  },
});

redux.listener.startListening({
  matcher: isAnyOf(quarantineInspectionPlan.fulfilled, releaseInspectionPlan.fulfilled),
  effect: (action, listenerApi) => {
    selectStatusLoading(false);
    listenerApi.dispatch(
      showToast({
        message: 'InspectionPlan saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchInspectionPlanData.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(INSPECTIONPLAN_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteInspectionPlan.fulfilled, deleteInspectionPlanByMasterId.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(INSPECTIONPLAN_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'InspectionPlan deleted successfully',
        type: 'success',
      }),
    );
    // Refetch the data to ensure UI is in sync with backend
    listenerApi.dispatch(fetchInspectionPlansWithFilters());
  },
});

redux.injectReducer(STORE_KEY, reducer);
